import { Button, Image, message } from "antd"
import PropTypes from "prop-types"
import React, { useMemo, useState } from "react"
import Geocode from "react-geocode"
import styled from "styled-components"

import AppTable from "../../_shared/components/AppTable"
import RoomAllocation from "../../_shared/components/RoomAllocation"
import { useStateValue } from "../../_shared/context/AppStateStore"
import * as translateResponse from "../../_shared/helpers/translateResponse"
import usePaginatedRestResource from "../../_shared/hooks/usePaginatedRestResource"
import { createRoomTenancy } from "../../api"

// ----------------------------------------------------------------------------

function _AgencyApp(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    Geocode.setApiKey("AIzaSyBwlx8Eit4Ch3iaTfzsKsxVfdlRoQ_h5wU")

    const defaultRange = 50

    const headers = [
        {
            title: "Data",
            dataIndex: "data",
            key: "data",
        },
        {
            title: "Nome e cognome",
            dataIndex: "fullName",
            key: "name",
        },
        {
            title: "Data di nascita",
            dataIndex: "birthDate",
            key: "birthday",
        },
        {
            title: "E-mail",
            dataIndex: "email",
            key: "email",
        },
        {
            title: "ID stanza",
            dataIndex: "roomID",
            key: "roomID",
        },
        {
            title: "",
            dataIndex: "link",
            key: "link",
        },
    ]

    // -------------------------------------
    // Hooks (e.g. useState, useMemo ...)
    // -------------------------------------

    const [{ user, token }] = useStateValue()

    const [selectedRequest, setSelectedRequest] = useState(null)

    const [rawRequests, fetchRawRequests, , rawRequestsPagination] =
        usePaginatedRestResource("/contact-requests", token)
    const [rawHandledRequests, fetchHandled, , handledRequestsPagination] =
        usePaginatedRestResource("/contact-requests", token, {
            defaultFilters: { handled: "1" },
        })
    const [rawRooms, , , , , roomFiltersChange] = usePaginatedRestResource(
        "/rooms",
        null,
        {
            filters: {
                managers: [user.id],
            },
        }
    )

    // -------------------------------------
    // Effects
    // -------------------------------------

    const requests = useMemo(() => {
        return rawRequests?.map((req) => {
            let r = translateResponse.jsonFormatContactRequest(req)
            r.link = ContactButton(r)
            return r
        })
        // eslint-disable-next-line
    }, [rawRequests])

    const handledRequests = useMemo(() => {
        return rawHandledRequests?.map((req) => {
            let r = translateResponse.jsonFormatContactRequest(req, true)
            r.link = ContactButton(r)
            return r
        })
        // eslint-disable-next-line
    }, [rawHandledRequests])

    const rooms = useMemo(() => {
        return rawRooms?.map((room) => {
            return translateResponse.jsonFormatRoom(room)
        })
        // eslint-disable-next-line
    }, [rawRooms])

    // -------------------------------------
    // Component functions
    // -------------------------------------

    function ContactButton(request) {
        return request.handled ? (
            <Button
                className="allocation"
                onClick={() => {
                    allocationRoom(request)
                }}
            >
                Assegna alloggio
            </Button>
        ) : (
            <Button
                className="reply"
                onClick={() => {
                    replyToRequest(request)
                }}
            >
                <Image src="/assets/icons/reply.svg" preview={false} /> Rispondi
            </Button>
        )
    }

    function allocationRoom(request) {
        setSelectedRequest(request)
    }

    function closeOverlay() {
        setSelectedRequest(false)
    }

    function replyToRequest(request) {
        window.open("mailto:" + request.email)
    }

    function search(location) {
        if (location != "") {
            Geocode.fromAddress(location).then((response) => {
                const pos = {
                    lat: response.results[0].geometry.location.lat,
                    lng: response.results[0].geometry.location.lng,
                    range: defaultRange,
                }
                roomFiltersChange({
                    ...pos,
                    max_distance: pos.range * 1000,
                    managers: user.id,
                })
            })
        } else {
            roomFiltersChange({
                managers: user.id,
                lat: null,
                lng: null,
                max_distance: null,
            })
        }
    }

    async function allocateRoom(values) {
        try {
            const body = {
                ...values,
                startDate: values.startDate.format("YYYY-MM-DD"),
                endDate: values.endDate.format("YYYY-MM-DD"),
                tenant: {
                    ...values.tenant,
                    birthDate: values.tenant.birthDate.format("YYYY-MM-DD"),
                },
            }
            await createRoomTenancy(body, token)
            setSelectedRequest(null)
            message.success("Richiesta creata con successo")
        } catch (e) {
            console.log(e)
            message.error("Errore durante l'elaborazione della richiesta")
        }
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    return (
        <div className={props.className}>
            <AppTable
                title="Richieste studenti"
                headers={headers}
                rows={requests}
                className="table"
                pagination={rawRequestsPagination}
                onChange={(pagination) => fetchRawRequests({ pagination })}
            />
            <AppTable
                title="Tutte le richieste"
                headers={headers}
                rows={handledRequests}
                pagination={handledRequestsPagination}
                className="table"
                onChange={(pagination) => fetchHandled({ pagination })}
            />

            <RoomAllocation
                title="Assegna alloggio"
                open={!!selectedRequest}
                snapPoint={75}
                onCancel={closeOverlay}
                rooms={rooms}
                search={search}
                allocateRoom={allocateRoom}
                requestInfo={selectedRequest}
            />
        </div>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

_AgencyApp.propTypes = {
    className: PropTypes.string.isRequired,
}

_AgencyApp.defaultProps = {}

// ----------------------------------------------------------------------------

const AgencyApp = styled(_AgencyApp)`
    & {
        .ant-btn {
            display: flex;
            align-items: center;
            justify-content: space-around;
            border: 2px solid ${(props) => props.theme.colors.dark};
            border-radius: 8px;
            font-weight: 600;
            width: 147px;
            height: 44px;
            margin: 0;
            &:hover {
                color: ${(props) => props.theme.colors.dark};
            }
            img {
                width: 26px;
            }
            &.allocation {
                color: ${(props) => props.theme.colors.orange};
                border-color: ${(props) => props.theme.colors.orange};
            }
        }
    }
`
// ----------------------------------------------------------------------------

export default AgencyApp
